/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { BreadcrumbStep } from '../hooks/useSEO';

interface BreadcrumbProps {
  /** Trail ending at the current page. The Home step is prepended automatically. */
  trail: BreadcrumbStep[];
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ trail }) => {
  const { language } = useLanguage();
  const home: BreadcrumbStep = { label: 'ERE-Vergelijk', path: `/${language}` };
  const items = [home, ...trail];

  return (
    <nav aria-label="Breadcrumb" className="bg-slate-950">
      <ol className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-xs font-medium overflow-x-auto">
        {items.map((step, i) => {
          const isLast = i === items.length - 1;
          const isHome = i === 0;
          return (
            <li key={`${step.path}-${i}`} className="flex items-center gap-2 whitespace-nowrap">
              {i > 0 && (
                <ChevronRight className="w-3.5 h-3.5 text-slate-700 shrink-0" aria-hidden="true" />
              )}
              {isLast ? (
                <span className="text-slate-300" aria-current="page">
                  {isHome ? <Home className="w-3.5 h-3.5 inline" aria-hidden="true" /> : step.label}
                </span>
              ) : (
                <Link
                  to={step.path}
                  className="text-fg-muted hover:text-brand-hover transition-colors inline-flex items-center gap-1.5"
                >
                  {isHome ? (
                    <>
                      <Home className="w-3.5 h-3.5" aria-hidden="true" />
                      <span className="sr-only">{step.label}</span>
                    </>
                  ) : (
                    step.label
                  )}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
